import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LazyLog } from '@lambdatestincprivate/react-lazylog';
import { LTLoader } from '@lambdatestincprivate/lt-components';
import {
  DownloadIcon,
  SearchIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ScreenFullIcon,
  ScreenNormalIcon,
  SidebarCollapseIcon,
  SidebarExpandIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  InfoIcon,
  XCircleFillIcon,
  AlertFillIcon,
  IssueOpenedIcon,
} from '@primer/octicons-react';

import { getEnvVar } from '../../utils/env';
import ComponentNoDataFound from '../ComponentNoDataFound';

// Same regexes hyperexecute uses for its Log Highlights categorization.
const HIGHLIGHT_CATEGORIES = [
  { key: 'error', name: 'Error', regex: /\b(error)\b/i, color: '#f85149', Icon: XCircleFillIcon },
  { key: 'failure', name: 'Failure', regex: /\b(fail|failure)\b/i, color: '#db6d28', Icon: IssueOpenedIcon },
  { key: 'warning', name: 'Warning', regex: /\b(warn|warning)\b/i, color: '#d29922', Icon: AlertFillIcon },
];

const safeGetEnvVar = (key) => {
  try {
    return getEnvVar(key);
  } catch (e) {
    return undefined;
  }
};

/**
 * Reads a REACT_APP_ env var, preferring (in order):
 *   1. The TestPageComponents env registry (populated by the host app).
 *   2. The runtime `process.env[key]` — when the host app is CRA, webpack's
 *      DefinePlugin replaces the literal access with the baked-in value.
 *   3. The runtime `window._env_[key]` — used by apps that inject envs via
 *      a public/env-config.js script at page load.
 *
 * This makes the component resilient to host apps that forget to call
 * `initializeTestPageComponentsEnv()` with the logistics host.
 */
const resolveEnvVar = (key, processEnvValue) => {
  const registryValue = safeGetEnvVar(key);
  if (registryValue) return registryValue;
  if (processEnvValue) return processEnvValue;
  if (typeof window !== 'undefined' && window?._env_?.[key]) {
    return window._env_[key];
  }
  return undefined;
};

const STAGE_STATUS_RUNNING = ['initiated', 'running', 'created', 'queued', 'in-progress', 'in progress'];

const makeBasicAuth = (username, accessKey) => {
  if (!username || !accessKey) return '';
  try {
    return 'Basic ' + btoa(`${username}:${accessKey}`);
  } catch (e) {
    return '';
  }
};

const safeFetchJson = async (url, headers) => {
  try {
    const res = await fetch(url, { headers });
    if (!res.ok) {
      return { ok: false, status: res.status, data: null };
    }
    const data = await res.json();
    return { ok: true, status: res.status, data };
  } catch (e) {
    return { ok: false, status: 0, data: null, error: e };
  }
};

const safeFetchText = async (url, headers) => {
  try {
    const res = await fetch(url, { headers });
    if (!res.ok) {
      return { ok: false, status: res.status, text: '' };
    }
    const text = await res.text();
    return { ok: true, status: res.status, text };
  } catch (e) {
    return { ok: false, status: 0, text: '', error: e };
  }
};

/**
 * Resolves the HyperExecute task metadata for a given LambdaTest testId.
 * Hits: {sentinelHost}/v1.0/test/{testId}
 * Returns: { job, task, stage_id, ... }
 */
const fetchHypertestMapping = async ({ sentinelHost, testId, authHeader }) => {
  const url = `${sentinelHost}/v1.0/test/${testId}`;
  const { ok, data } = await safeFetchJson(url, { Authorization: authHeader });
  if (!ok) return null;
  return data?.data || null;
};

/**
 * Fetches the stage list for a task and finds the scenario stage matching stageId.
 * Hits: {sentinelHost}/v1.0/stage/{taskId}
 * Returns the matching stage object containing { id, order, link, type, status, created_at, ... }
 */
const fetchScenarioStage = async ({ sentinelHost, taskId, stageId, authHeader }) => {
  const url = `${sentinelHost}/v1.0/stage/${taskId}`;
  const { ok, data } = await safeFetchJson(url, { Authorization: authHeader });
  if (!ok) return null;
  const stages = data?.data || [];
  return stages.find((s) => s?.id === stageId) || null;
};

/**
 * Fetches the actual scenario log text using hyperexecute's CDN-first strategy
 * with a fallback to the direct stage-logs endpoint.
 *
 * 1. CDN: GET {logisticsHost}/v1.0/stage/{stageId}/logs/cdn/redirect → { data: <blobUrl> }
 *         then GET <blobUrl> as text.
 * 2. Fallback: GET {logisticsHost}/v1.0/stage/{stageId}/logs (auth'd) → JSON/text.
 */
const fetchScenarioLogText = async ({ logisticsHost, stageId, authHeader }) => {
  const cdnRedirectUrl = `${logisticsHost}/v1.0/stage/${stageId}/logs/cdn/redirect`;
  const cdnResp = await safeFetchJson(cdnRedirectUrl, { Authorization: authHeader });
  const blobUrl = cdnResp?.data?.data;
  if (cdnResp.ok && typeof blobUrl === 'string' && blobUrl) {
    const blob = await safeFetchText(blobUrl, {});
    if (blob.ok && blob.text) {
      return blob.text;
    }
  }

  const fallbackUrl = `${logisticsHost}/v1.0/stage/${stageId}/logs`;
  const fallbackResp = await safeFetchText(fallbackUrl, { Authorization: authHeader });
  if (fallbackResp.ok && fallbackResp.text) {
    try {
      const parsed = JSON.parse(fallbackResp.text);
      if (parsed && typeof parsed.data === 'string') return parsed.data;
      if (parsed && typeof parsed === 'string') return parsed;
    } catch (e) {
      // Not JSON — return raw text
    }
    return fallbackResp.text;
  }

  return null;
};

/**
 * Fetches running-stage scenario logs using the chunked rawlogs endpoint.
 * Hits: {logisticsHost}/v1.0/rawlogs/{taskId}?name={chunkLink}
 * Iterates chunks command.{order}.0.log, command.{order}.1.log, ... until
 * a chunk is empty/404. Returns concatenated text.
 */
const fetchScenarioLogChunks = async ({
  logisticsHost,
  taskId,
  order,
  authHeader,
  maxChunks = 8,
}) => {
  const parts = [];
  for (let i = 0; i < maxChunks; i += 1) {
    const link = `command.${order}.${i}.log`;
    const url = `${logisticsHost}/v1.0/rawlogs/${taskId}?name=${link}`;
    const resp = await safeFetchJson(url, { Authorization: authHeader });
    if (!resp.ok) break;
    const chunk = resp?.data?.data;
    if (!chunk) break;
    parts.push(typeof chunk === 'string' ? chunk : String(chunk));
  }
  return parts.length ? parts.join('') : null;
};

const TOOLBAR_BG = '#161b22';
const TOOLBAR_BORDER = '#30363d';
const LOG_BG = '#0d1117';
const LOG_FG = '#e6edf3';
const MUTED_FG = '#8b949e';

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  minHeight: '300px',
  backgroundColor: LOG_BG,
};

const fullscreenStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  backgroundColor: LOG_BG,
};

const toolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  borderBottom: `1px solid ${TOOLBAR_BORDER}`,
  backgroundColor: TOOLBAR_BG,
  flex: '0 0 auto',
};

const iconBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  border: `1px solid ${TOOLBAR_BORDER}`,
  backgroundColor: 'transparent',
  color: LOG_FG,
  cursor: 'pointer',
  padding: 0,
  flex: '0 0 auto',
};

const iconBtnDisabledStyle = {
  ...iconBtnStyle,
  opacity: 0.4,
  cursor: 'not-allowed',
};

const searchWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 auto',
  minWidth: 0,
  backgroundColor: LOG_BG,
  border: `1px solid ${TOOLBAR_BORDER}`,
  borderRadius: '6px',
  padding: '0 10px',
  height: '32px',
  gap: '8px',
};

const searchInputStyle = {
  flex: '1 1 auto',
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: LOG_FG,
  fontSize: '13px',
  fontFamily: 'inherit',
  padding: 0,
};

const matchesTextStyle = {
  color: MUTED_FG,
  fontSize: '12px',
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
};

const bodyRowStyle = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'row',
  position: 'relative',
  backgroundColor: LOG_BG,
};

const logPaneStyle = {
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 0,
  position: 'relative',
};

const highlightsPaneStyle = {
  flex: '0 0 260px',
  minHeight: 0,
  borderLeft: `1px solid ${TOOLBAR_BORDER}`,
  backgroundColor: TOOLBAR_BG,
  color: LOG_FG,
  overflowY: 'auto',
  padding: '12px 14px',
};

const highlightsHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: LOG_FG,
  marginBottom: '12px',
};

const highlightGroupHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  padding: '6px 4px',
  borderRadius: '4px',
  fontSize: '13px',
  color: LOG_FG,
  userSelect: 'none',
};

const lazyLogStyle = {
  backgroundColor: LOG_BG,
  color: LOG_FG,
  fontFamily:
    'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
};

const emptyStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  backgroundColor: '#ffffff',
  color: '#24292f',
};

// Computes which 1-indexed line numbers contain the search term
// (case-insensitive), so we can pass them as `highlight` to LazyLog.
const computeMatches = (text, term) => {
  if (!term) return [];
  const lower = term.toLowerCase();
  const lines = text.split('\n');
  const matches = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].toLowerCase().indexOf(lower) !== -1) {
      matches.push(i + 1);
    }
  }
  return matches;
};

// Computes the line numbers that match each highlight category's regex.
// Returns an object keyed by category.key with arrays of 1-indexed line numbers.
const computeCategoryHits = (text) => {
  const lines = text.split('\n');
  const out = {};
  HIGHLIGHT_CATEGORIES.forEach((cat) => {
    out[cat.key] = [];
  });
  for (let i = 0; i < lines.length; i += 1) {
    const ln = lines[i];
    HIGHLIGHT_CATEGORIES.forEach((cat) => {
      if (cat.regex.test(ln)) {
        out[cat.key].push(i + 1);
      }
    });
  }
  return out;
};

// Scrolls the internal virtualized list of react-lazylog to the top or bottom.
// The private lazylog exposes no imperative API, so we locate its DOM node via
// the `.react-lazylog` class (same technique hyperexecute's handleScrollArrowClick uses).
const scrollLazyLogContainer = (root, direction) => {
  const el = root?.querySelector?.('.react-lazylog');
  if (!el) return;
  try {
    el.scrollTo({
      top: direction === 'up' ? 0 : el.scrollHeight,
      behavior: 'smooth',
    });
  } catch (e) {
    el.scrollTop = direction === 'up' ? 0 : el.scrollHeight;
  }
};

/**
 * Shared scenario-logs viewer that shows the HyperExecute scenario logs
 * for the *current* LambdaTest test. Used by both TestPageApp (mobile)
 * and TestPageWeb inside the Kane AI dock.
 *
 * The component is self-contained: it makes direct REST calls to the
 * HyperExecute sentinel/logistics APIs and renders the result with
 * react-lazylog (search + line numbers + virtualization).
 */
const HyperExecuteScenarioLogs = ({
  testId,
  username,
  accessKey,
  sentinelHost: sentinelHostProp,
  logisticsHost: logisticsHostProp,
}) => {
  const [state, setState] = useState({
    loading: true,
    error: null,
    logText: '',
    mapping: null,
    stage: null,
  });

  const sentinelHost = useMemo(() => {
    const fromProp = sentinelHostProp || '';
    if (fromProp) return fromProp.replace(/\/$/, '');
    const fromEnv = resolveEnvVar(
      'REACT_APP_HYPERTEST_SENTINEL_API_ENDPOINT',
      process.env.REACT_APP_HYPERTEST_SENTINEL_API_ENDPOINT
    );
    return (fromEnv || '').replace(/\/$/, '');
  }, [sentinelHostProp]);

  const logisticsHost = useMemo(() => {
    const fromProp = logisticsHostProp || '';
    if (fromProp) return fromProp.replace(/\/$/, '');
    const fromEnv = resolveEnvVar(
      'REACT_APP_HYPERTEST_LOGISTIC_API_ENDPOINT',
      process.env.REACT_APP_HYPERTEST_LOGISTIC_API_ENDPOINT
    );
    return (fromEnv || '').replace(/\/$/, '');
  }, [logisticsHostProp]);

  const authHeader = useMemo(
    () => makeBasicAuth(username, accessKey),
    [username, accessKey]
  );

  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    // eslint-disable-next-line no-console
    console.debug('[HyperExecuteScenarioLogs] resolved config', {
      testId,
      sentinelHost,
      logisticsHost,
      hasAuth: !!authHeader,
    });
    const missing = [];
    if (!testId) missing.push('testId');
    if (!sentinelHost) missing.push('REACT_APP_HYPERTEST_SENTINEL_API_ENDPOINT');
    if (!logisticsHost) missing.push('REACT_APP_HYPERTEST_LOGISTIC_API_ENDPOINT');
    if (!authHeader) missing.push('username/apiToken');
    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn(
        '[HyperExecuteScenarioLogs] skipping fetch; missing:',
        missing
      );
      setState({
        loading: false,
        error: `HyperExecute configuration is not available (missing: ${missing.join(', ')}).`,
        logText: '',
        mapping: null,
        stage: null,
      });
      return () => {
        abortRef.current = true;
      };
    }

    setState((prev) => ({ ...prev, loading: true, error: null, logText: '' }));

    const run = async () => {
      const mapping = await fetchHypertestMapping({
        sentinelHost,
        testId,
        authHeader,
      });
      if (abortRef.current) return;
      if (!mapping || !mapping.task || !mapping.stage_id) {
        setState({
          loading: false,
          error:
            'No HyperExecute task is associated with this test yet. Logs may appear once the run starts.',
          logText: '',
          mapping,
          stage: null,
        });
        return;
      }

      const stage = await fetchScenarioStage({
        sentinelHost,
        taskId: mapping.task,
        stageId: mapping.stage_id,
        authHeader,
      });
      if (abortRef.current) return;
      if (!stage) {
        setState({
          loading: false,
          error: 'Could not find the scenario stage for this test.',
          logText: '',
          mapping,
          stage: null,
        });
        return;
      }

      const isRunning = STAGE_STATUS_RUNNING.includes(
        (stage.status || '').toLowerCase()
      );

      let logText = null;
      if (isRunning && stage.order !== undefined && stage.order !== null) {
        logText = await fetchScenarioLogChunks({
          logisticsHost,
          taskId: mapping.task,
          order: stage.order,
          authHeader,
        });
      }
      if (logText == null) {
        logText = await fetchScenarioLogText({
          logisticsHost,
          stageId: mapping.stage_id,
          authHeader,
        });
      }

      if (abortRef.current) return;
      if (logText == null) {
        setState({
          loading: false,
          error: 'Failed to load HyperExecute scenario logs.',
          logText: '',
          mapping,
          stage,
        });
        return;
      }

      setState({
        loading: false,
        error: null,
        logText: logText || ' ',
        mapping,
        stage,
      });
    };

    run();

    return () => {
      abortRef.current = true;
    };
  }, [testId, sentinelHost, logisticsHost, authHeader]);

  // Toolbar / sidebar state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const rootRef = useRef(null);

  // Reset transient UI state whenever a fresh log loads for a new test.
  useEffect(() => {
    setSearchTerm('');
    setCurrentMatchIdx(0);
    setExpandedCats({});
  }, [state.logText]);

  const matches = useMemo(
    () => computeMatches(state.logText || '', searchTerm),
    [state.logText, searchTerm]
  );

  const categoryHits = useMemo(
    () => computeCategoryHits(state.logText || ''),
    [state.logText]
  );

  // The line to highlight / scroll-to in LazyLog — either the current search
  // match or the most-recently-clicked highlight-category line.
  const [jumpLine, setJumpLine] = useState(null);

  // When the user types in search, jump to the first match automatically.
  useEffect(() => {
    if (matches.length > 0) {
      setCurrentMatchIdx(0);
      setJumpLine(matches[0]);
    } else {
      setJumpLine(null);
    }
  }, [matches]);

  const highlightLines = useMemo(() => {
    if (jumpLine) return [jumpLine];
    return [];
  }, [jumpLine]);

  const goToMatch = useCallback(
    (direction) => {
      if (!matches.length) return;
      const next =
        direction === 'next'
          ? (currentMatchIdx + 1) % matches.length
          : (currentMatchIdx - 1 + matches.length) % matches.length;
      setCurrentMatchIdx(next);
      setJumpLine(matches[next]);
    },
    [matches, currentMatchIdx]
  );

  const handleScroll = useCallback(
    (direction) => {
      scrollLazyLogContainer(rootRef.current, direction);
    },
    []
  );

  const handleDownload = useCallback(() => {
    try {
      const stageId = state.mapping?.stage_id || 'log';
      const blob = new Blob([state.logText || ''], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hyperexecute-scenario-${stageId}.log`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[HyperExecuteScenarioLogs] download failed', e);
    }
  }, [state.logText, state.mapping]);

  const toggleCategory = useCallback((key) => {
    setExpandedCats((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const jumpToLine = useCallback((lineNumber) => {
    setJumpLine(lineNumber);
  }, []);

  // Exit fullscreen on Escape — matches the usual fullscreen UX.
  useEffect(() => {
    if (!isFullScreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullScreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullScreen]);

  const renderToolbar = () => (
    <div style={toolbarStyle}>
      <button
        type="button"
        style={iconBtnStyle}
        onClick={handleDownload}
        aria-label="Download logs"
        title="Download logs"
      >
        <DownloadIcon size={16} />
      </button>

      <div style={searchWrapperStyle}>
        <SearchIcon size={14} aria-hidden="true" />
        <input
          type="text"
          placeholder="Search logs"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={searchInputStyle}
          aria-label="Search logs"
        />
        <span style={matchesTextStyle}>
          {searchTerm
            ? `${matches.length > 0 ? currentMatchIdx + 1 + '/' : ''}${matches.length} match${matches.length === 1 ? '' : 'es'}`
            : '0 matches'}
        </span>
      </div>

      {/* Single pair of up/down arrows: when searching, they navigate matches;
          otherwise they scroll the log pane to top/bottom. */}
      <button
        type="button"
        style={iconBtnStyle}
        onClick={() => (searchTerm ? goToMatch('prev') : handleScroll('up'))}
        aria-label={searchTerm ? 'Previous match' : 'Scroll to top'}
        title={searchTerm ? 'Previous match' : 'Scroll to top'}
      >
        <ArrowUpIcon size={16} />
      </button>
      <button
        type="button"
        style={iconBtnStyle}
        onClick={() => (searchTerm ? goToMatch('next') : handleScroll('down'))}
        aria-label={searchTerm ? 'Next match' : 'Scroll to bottom'}
        title={searchTerm ? 'Next match' : 'Scroll to bottom'}
      >
        <ArrowDownIcon size={16} />
      </button>

      <button
        type="button"
        style={iconBtnStyle}
        onClick={() => setIsFullScreen((v) => !v)}
        aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
        title={isFullScreen ? 'Exit full screen' : 'Full screen'}
      >
        {isFullScreen ? <ScreenNormalIcon size={16} /> : <ScreenFullIcon size={16} />}
      </button>

      <button
        type="button"
        style={iconBtnStyle}
        onClick={() => setShowHighlights((v) => !v)}
        aria-label={showHighlights ? 'Hide log highlights' : 'Show log highlights'}
        title={showHighlights ? 'Hide log highlights' : 'Show log highlights'}
      >
        {showHighlights ? <SidebarExpandIcon size={16} /> : <SidebarCollapseIcon size={16} />}
      </button>
    </div>
  );

  const renderHighlightsPanel = () => (
    <aside style={highlightsPaneStyle} aria-label="Log highlights">
      <div style={highlightsHeaderStyle}>
        Log Highlights
        <InfoIcon size={14} />
      </div>
      {HIGHLIGHT_CATEGORIES.map((cat) => {
        const hits = categoryHits[cat.key] || [];
        const isOpen = !!expandedCats[cat.key];
        return (
          <div key={cat.key} style={{ marginBottom: '4px' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleCategory(cat.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleCategory(cat.key);
                }
              }}
              style={highlightGroupHeaderStyle}
              aria-expanded={isOpen}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {isOpen ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                <cat.Icon size={14} fill={cat.color} />
                <span style={{ fontWeight: 600 }}>{cat.name}</span>
              </span>
              <span style={{ color: MUTED_FG, fontSize: '12px' }}>{hits.length}</span>
            </div>
            {isOpen && hits.length > 0 ? (
              <div style={{ paddingLeft: '22px', marginTop: '4px' }}>
                {hits.slice(0, 50).map((lineNo) => (
                  <div
                    key={lineNo}
                    role="button"
                    tabIndex={0}
                    onClick={() => jumpToLine(lineNo)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        jumpToLine(lineNo);
                      }
                    }}
                    style={{
                      fontSize: '12px',
                      color: MUTED_FG,
                      padding: '2px 0',
                      cursor: 'pointer',
                    }}
                  >
                    Line {lineNo}
                  </div>
                ))}
                {hits.length > 50 ? (
                  <div style={{ fontSize: '11px', color: MUTED_FG, paddingTop: '4px' }}>
                    +{hits.length - 50} more
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </aside>
  );

  // Loading and error states take over the full container on a neutral
  // background — otherwise the loader/empty state sits awkwardly on the
  // terminal-dark log theme.
  if (state.loading) {
    return (
      <div
        ref={rootRef}
        style={{ ...containerStyle, backgroundColor: '#ffffff' }}
        role="region"
        aria-label="HyperExecute scenario logs"
      >
        <div style={emptyStyle}>
          <LTLoader heading="Fetching HyperExecute logs" />
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        ref={rootRef}
        style={{ ...containerStyle, backgroundColor: '#ffffff' }}
        role="region"
        aria-label="HyperExecute scenario logs"
      >
        <div style={emptyStyle}>
          <ComponentNoDataFound type="selenium-logs" message={state.error} hideHelpLink />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      style={isFullScreen ? { ...containerStyle, ...fullscreenStyle } : containerStyle}
      role="region"
      aria-label="HyperExecute scenario logs"
    >
      {renderToolbar()}
      <div style={bodyRowStyle}>
        <div style={logPaneStyle}>
          <LazyLog
            style={lazyLogStyle}
            text={state.logText}
            caseInsensitive
            extraLines={1}
            selectableLines
            highlight={highlightLines}
            scrollToLine={jumpLine || 0}
          />
        </div>
        {showHighlights ? renderHighlightsPanel() : null}
      </div>
    </div>
  );
};

export default HyperExecuteScenarioLogs;
